import {
  AccountAddressAttr,
  AccountAttr,
  IOrders,
  JsonApiDocument,
  LocalizedSlugs,
  OrderAttr,
  RelationType,
  TaxonAttr,
  Wishlist,
  WishlistAttr,
} from '../services/spree-client/storefront';
import {
  CustomerWishlist,
  Gender,
  Order,
  OrdersData,
  PaymentState,
  ShipmentState,
  User,
  YesClubState,
} from '../pages/customer/customer.types';
import { Address, LineItem } from '../pages/cart-page/cart.types';
import {
  convertPriceToFloat,
  deserializeLineItem,
  filterIncludedLineItems,
  findAttachment,
  findImagesBasedOnVariantId,
  formatOptions,
} from './line-items.serializer';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { taxonToCategory } from './category.serializer';
import { YES_CLUB } from '../helpers/consts';

export const deserializeOrderList = (orders: IOrders): OrdersData => {
  let links = null;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (orders && orders['links']) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    links = orders['links'];
  }

  return {
    ordersPage: orders.data.map((order) => deserializeOrder(order, orders.included)),
    meta: {
      ...links,
      count: orders.meta.count,
      totalCount: orders.meta.total_count,
      totalPages: orders.meta.total_pages,
    },
  };
};

export const deserializeWishlist = (wishlist: Wishlist): CustomerWishlist => {
  let wishedItems = [] as LineItem[];

  if (wishlist.included) {
    wishedItems = filterIncludedWishedItems(wishlist.included, wishlist.data)
      .map((item) => deserializeWishedItem(item, wishlist.included!))
      .filter((item): item is LineItem => item !== null);
  }

  return {
    id: wishlist.data.id,
    token: wishlist.data.attributes.token,
    name: wishlist.data.attributes.name,
    isPrivate: wishlist.data.attributes.is_private,
    isDefault: wishlist.data.attributes.is_default,
    wishedItems: wishedItems,
  };
};

export const deserializeWishedItem = (
  item: JsonApiDocument,
  include: JsonApiDocument[],
): LineItem | null => {
  const variantId = (item.relationships['variant'].data as RelationType).id;

  if (!include) {
    return null;
  }

  const variant = findAttachment(include, variantId, 'variant');

  if (!variant) {
    return null;
  }

  const productId = (variant.relationships['product'].data as RelationType).id;
  const product = findAttachment(include, productId, 'product');

  if (!product) {
    return null;
  }

  const image = findImagesBasedOnVariantId(include, variantId);
  const taxonIds = ((product?.relationships['taxons'].data ?? []) as RelationType[]).map(
    (t) => t.id,
  );
  const taxons = taxonIds
    .map((id) => findAttachment(include, id, 'taxon'))
    .filter(Boolean) as TaxonAttr[];
  const categories = taxons.map((taxon) => taxonToCategory(taxon));

  return {
    id: parseInt(item.id, 10),
    productId: parseInt(productId, 10),
    variantId: parseInt(variantId, 10),
    description: product.attributes['description'] as string,
    name: product.attributes['name'] as string,
    sku: variant.attributes['sku'] as string,
    slug: product.attributes['slug'] as string,
    image: image,
    price: {
      original: convertPriceToFloat(variant.attributes['compare_at_price']),
      current: convertPriceToFloat(variant.attributes['price']),
    },
    localizedSlugs: product.attributes['localized_slugs'] as LocalizedSlugs,
    purchasable: variant.attributes['purchasable'] as boolean,
    inStock: variant.attributes['in_stock'] as boolean,
    displayPrice: variant.attributes['display_price'] as string,
    displayTotal: variant.attributes['display_total'] as string,
    quantity: item.attributes['quantity'] as number,
    options: formatOptions(variant.attributes['options_text'] as string),
    optionsText: variant.attributes['options_text'] as string,
    categories,
  };
};

export const deserializeOrder = (
  order: OrderAttr,
  attachments: JsonApiDocument[] | undefined,
): Order => {
  let lineItems: LineItem[] = [];

  if (attachments) {
    lineItems = filterIncludedLineItems(attachments, order).map((item) =>
      deserializeLineItem(item, attachments),
    );
  }

  return {
    id: order.id,
    number: order.attributes.number,
    total: order.attributes.total,
    shipTotal: order.attributes.ship_total,
    createdAt: new Date(Date.parse(order.attributes.created_at.toString())),
    updatedAt: new Date(Date.parse(order.attributes.created_at.toString())),
    completedAt: new Date(Date.parse(order.attributes.created_at.toString())),
    currency: order.attributes.currency,
    state: order.attributes.state,
    promoTotal: order.attributes.promo_total,
    itemCount: order.attributes.item_count,
    specialInstructions: order.attributes.special_instructions,
    shipmentState: order.attributes[
      'shipment_state'
    ] as (typeof ShipmentState)[keyof typeof ShipmentState],
    paymentState: order.attributes[
      'payment_state'
    ] as (typeof PaymentState)[keyof typeof PaymentState],
    completionDate: order.attributes.public_metadata['completion_date'] as string,
    lineItems: lineItems,
    shippingAddress: deserializeOrderAddress(
      findAttachments(order, attachments, 'shipping_address', 'address'),
    ),
    billingAddress: deserializeOrderAddress(
      findAttachments(order, attachments, 'billing_address', 'address'),
    ),
    paymentMethodName: deserializeOrderPayment(order, attachments),
    shipmentMethodName: deserializeOrderShipment(order, attachments),
  };
};

export const deserializeOrderUser = (orders: IOrders): User | null => {
  if (!orders || !orders.included) {
    return null;
  }

  const user = orders.included.find((e) => e.type === 'user');

  if (!user) {
    return null;
  }

  return deserializeUser(user);
};

export const deserializeUser = (
  user: AccountAttr | JsonApiDocument | undefined,
): User | null => {
  if (!user || !user.attributes) {
    return null;
  }

  const metadata = user.attributes['public_metadata'] as Record<string, unknown>;

  const smsConsent = (metadata['sms_consent'] as string) === 'true';
  const emailConsent = (metadata['email_consent'] as string) === 'true';
  const typeDataBilling = user.relationships['default_billing_address']?.data;
  const defaultBillingAddressId = Array.isArray(typeDataBilling)
    ? typeDataBilling[0]?.id
    : typeDataBilling?.id;
  const typeDataShipping = user.relationships['default_shipping_address']?.data;
  const defaultShippingAddressId = Array.isArray(typeDataShipping)
    ? typeDataShipping[0]?.id
    : typeDataShipping?.id;

  const birthday = new Date(metadata['birthday'] as string);

  return {
    email: (user.attributes['email'] as string) ?? '',
    firstName: (user.attributes['first_name'] as string) ?? '',
    lastName: (user.attributes['last_name'] as string) ?? '',
    selectedLocale: (user.attributes['selected-locale'] as string) ?? null,
    storeCredits: (user.attributes['store_credits'] as string) ?? ' ',
    completedOrders: (user.attributes['completed_orders'] as number) ?? 0,
    birthday: isNaN(birthday.getTime()) ? null : birthday,
    gender: (metadata['gender'] as Gender) ?? '',
    smsConsent: smsConsent,
    emailConsent: emailConsent,
    defaultBillingAddressId: defaultBillingAddressId,
    defaultShippingAddressId: defaultShippingAddressId,
    yesClubState: deserializeClubState(metadata),
  };
};

const deserializeClubState = (metadata: Record<string, unknown>): YesClubState => {
  const cardNumber = metadata[YES_CLUB.CARD_NUMBER] as string | undefined;
  const cardActive = metadata[YES_CLUB.CARD_ACTIVE] ?? (false as boolean | string);

  if (!cardNumber) {
    return { cardNumber, isCardActive: false };
  }

  return {
    cardNumber,
    isCardActive: typeof cardActive === 'string' ? JSON.parse(cardActive) : cardActive,
  };
};

export const findAttachments = (
  data: JsonApiDocument,
  attachments: JsonApiDocument[] | undefined,
  type: string,
  attachmentsType: string,
): JsonApiDocument | null => {
  if (!attachments) {
    return null;
  }

  const typeData = data.relationships[type]?.data;
  const id = Array.isArray(typeData) ? typeData[0]?.id : typeData?.id;

  return id
    ? (attachments.find((e) => e.id === id && e.type === attachmentsType) ?? null)
    : null;
};

export const deserializeOrderPayment = (
  order: JsonApiDocument,
  attachments: JsonApiDocument[] | undefined,
): string | null => {
  const paymentMethod = attachments
    ? findAttachments(order, attachments, 'payments', 'payment')
    : null;

  return paymentMethod
    ? (paymentMethod.attributes['payment_method_name'] as string)
    : null;
};

export const deserializeOrderShipment = (
  order: JsonApiDocument,
  attachments: JsonApiDocument[] | undefined,
): string | null => {
  const shipmentMethod = attachments
    ? findAttachments(order, attachments, 'shipments', 'shipment')
    : null;

  const selectedShippingRate = shipmentMethod
    ? findAttachments(
        shipmentMethod,
        attachments,
        'selected_shipping_rate',
        'shipping_rate',
      )
    : null;

  return selectedShippingRate
    ? (selectedShippingRate.attributes['name'] as string)
    : null;
};

export const deserializeOrderAddresses = (
  addresses: AccountAddressAttr[] | null,
): Address[] => {
  if (!addresses) {
    return [];
  }

  return addresses
    .map((address) => deserializeOrderAddress(address))
    .filter((address): address is Address => address !== null);
};

export const deserializeOrderAddress = (
  address: JsonApiDocument | null,
): Address | null => {
  if (!address) {
    return null;
  }

  return {
    _id: address.id,
    firstName: (address.attributes['firstname'] ?? '') as string,
    lastName: (address.attributes['lastname'] ?? '') as string,
    company: (address.attributes['company'] ?? '') as string,
    addressLine1: (address.attributes['address1'] ?? '') as string,
    addressLine2: (address.attributes['address2'] ?? '') as string,
    postalCode: (address.attributes['zipcode'] ?? '') as string,
    city: (address.attributes['city'] ?? '') as string,
    state: (address.attributes['state_name'] ?? '') as string,
    country: (address.attributes['country_iso'] ?? '') as string,
    phone: (address.attributes['phone'] ?? '') as string,
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function filterIncludedWishedItems(
  included: JsonApiDocument[],
  apiCart:
    | (WishlistAttr & JsonApiDocument)
    | (WishlistAttr & JsonApiDocument & JsonApiDocument[]),
) {
  const cartItemIds = (apiCart.relationships['wished_items'].data as RelationType[]).map(
    (l) => l.id,
  );

  return included.filter((e) => e.type === 'wished_item' && cartItemIds.includes(e.id));
}

export const errorControlForm = (error: HttpErrorResponse, form: FormGroup): void => {
  const errors = error.error.errors;

  if (!errors) {
    return;
  }

  const objectKeys = Object.keys(errors);

  objectKeys.forEach((key) => {
    const checkedKey = errorControlKey(key);
    const addressControl = form.get(checkedKey);

    if (addressControl) {
      addressControl.setErrors({
        [errors[key][0]]: true,
      });
    }
  });
};

export const errorControlKey = (key: string): string => {
  switch (key) {
    case 'firstname':
      return 'firstName';
    case 'lastname':
      return 'lastName';
    case 'address1':
      return 'addressLine1';
    case 'zipcode':
      return 'postalCode';
    default:
      return key;
  }
};
